import * as React from 'react';
import {CSSProperties} from 'react';
import {Form, Select, Tag} from 'antd';
import ExchangeRateRefreshButton from '../common/ExchangeRateRefreshButton';
import {FormComponentProps} from 'antd/lib/form';
import {Item} from '../api/dtos/dtos';
import locales from '../locales';
import InputFormItem from '../common/form-items/InputFormItem';
import AutoCompleteFormItem from '../common/form-items/AutoCompleteFormItem';
import TextAreaFormItem from '../common/form-items/TextAreaFormItem';
import NumberFormItem from '../common/form-items/NumberFormItem';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import SectionHeaderFormItem from '../common/form-items/SectionHeaderFormItem';
import {groupNumber, numberGroupFormatter, numberGroupParser} from '../utils/number-utils';
import StaticFormItem from "../common/form-items/StaticFormItem";
import {renderStateElement} from "../common/state";
import CheckBoxFormItem from "../common/form-items/CheckBoxFormItem";
import SelectFormItem from "../common/form-items/SelectFormItem";
import {getItemTypeOptions, ItemType} from "./itemType";
import ClientNameAutoCompleteFormItem from "../common/form-items/ClientNameAutoCompleteFormItem";

import {calculateHufSum, CURRENCY_EUR, CURRENCY_HUF, getCurrencyOptions} from "../utils/currency-utils";

interface Props extends FormComponentProps {
  editable: boolean;
  categories: string[],
  item?: Item;
  onRefreshExchangeRate: () => Promise<number | void>;
  onFormSubmit: (item: Item) => void;
  onFormCancel: () => void;
}

const FIELD_ITEM_TYPE = 'itemType';
const FIELD_INFINITE_STOCK = 'infiniteStock';
const FIELD_CHECKLIST_REQUIRED = 'checklistRequired';
const FIELD_INVENTORY_NUMBER = 'inventoryNumber';
const FIELD_NAME = 'name';
const FIELD_CATEGORY = 'category';
const FIELD_RESOURCE = 'resource';
const FIELD_DETAILS = 'details';
const FIELD_COMMENT = 'comment';
const FIELD_SERIAL = 'serial';
const FIELD_PRICE_HOURLY = 'priceHourly';
const FIELD_PRICE_DAILY = 'priceDaily';
const FIELD_PRICE_MONTHLY = 'priceMonthly';
const FIELD_PRICE_MM = 'priceMm';
const FIELD_PRICE_PCS = 'pricePcs';
const FIELD_ITEM_VALUE = 'itemValue';
const FIELD_ACQUISITION_VALUE = 'acquisitionValue';
const FIELD_ACQUISITION_CURRENCY = 'acquisitionCurrency';
const FIELD_ACQUISITION_EXCHANGE_RATE = 'acquisitionExchangeRate';
const FIELD_INVOICING_SUFFIX = 'invoicingSuffix';
const FIELD_EXTERNAL = 'external';
const FIELD_EXTERNAL_OWNER = 'externalOwner';


class ItemForm extends React.Component<Props, {}> {

  public render() {
    const {form, editable, item, categories} = this.props;
    const disabled = !editable;
    const createMode = item === undefined;
    const infiniteStockFieldStyle: CSSProperties = {
      display: this.isDeviceItemType() ? 'inherit' : 'none'
    };
    const external = form.getFieldValue(FIELD_EXTERNAL);
    const currency = form.getFieldValue(FIELD_ACQUISITION_CURRENCY) || item?.acquisitionCurrency || CURRENCY_HUF;
    const isEur = currency === CURRENCY_EUR;
    const acquisitionValue = form.getFieldValue(FIELD_ACQUISITION_VALUE);
    const exchangeRate = form.getFieldValue(FIELD_ACQUISITION_EXCHANGE_RATE);
    const hufValue = acquisitionValue === 0 ? 0 : calculateHufSum(acquisitionValue, currency, exchangeRate);


    return (
      <Form>
        {item && item.state && (
          <StaticFormItem
            label={locales.state}
            initialValue={renderStateElement(item.state)}
          />
        )}

        <SelectFormItem
          form={form}
          label={locales.type}
          fieldId={FIELD_ITEM_TYPE}
          initialValue={item ? item.itemType : ItemType.DEVICE}
          options={getItemTypeOptions()}
          required={true}
          disabled={!createMode}
        />

        <CheckBoxFormItem
          style={infiniteStockFieldStyle}
          form={form}
          fieldId={FIELD_INFINITE_STOCK}
          label={locales.infinite_stock}
          initialValue={item ? item.infiniteStock : null}
          required={false}
          disabled={!createMode}
        />

        <CheckBoxFormItem
          style={infiniteStockFieldStyle}
          form={form}
          fieldId={FIELD_CHECKLIST_REQUIRED}
          label={locales.checklist_required}
          initialValue={item ? item.checklistRequired : null}
          required={false}
          disabled={disabled}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_NAME}
          label={locales.appellation}
          initialValue={item ? item.name : null}
          required={true}
          disabled={disabled}
        />

        <AutoCompleteFormItem
          form={form}
          fieldId={FIELD_CATEGORY}
          label={locales.category}
          initialValue={item ? item.category : null}
          required={true}
          disabled={disabled}
          dataSource={categories}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_INVENTORY_NUMBER}
          label={locales.inventoryNumber}
          initialValue={item ? item.inventoryNumber : null}
          required={false}
          disabled={disabled}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_RESOURCE}
          label={locales.resource}
          initialValue={item ? item.resource : null}
          required={false}
          disabled={disabled}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_DETAILS}
          label={locales.properties}
          initialValue={item ? item.details : null}
          required={false}
          disabled={disabled}
        />

        <TextAreaFormItem
          form={form}
          fieldId={FIELD_COMMENT}
          label={locales.comment}
          initialValue={item ? item.comment : null}
          required={false}
          disabled={disabled}
        />

        <InputFormItem
          form={form}
          fieldId={FIELD_SERIAL}
          label={locales.serial}
          initialValue={item ? item.serial : null}
          required={false}
          disabled={disabled}
        />

        <NumberFormItem
          form={form}
          fieldId={FIELD_ACQUISITION_VALUE}
          label={locales.item_acquisition_value}
          initialValue={item?.acquisitionValue}
          required={false}
          disabled={disabled}
          min={0}
          max={1_000_000_000}
          precision={isEur ? 2 : 0}
          parser={numberGroupParser}
          formatter={numberGroupFormatter}
        >
          <span style={{marginLeft: 8, display: 'inline-block'}}>
            {form.getFieldDecorator(FIELD_ACQUISITION_CURRENCY, {
              initialValue: item?.acquisitionCurrency || CURRENCY_HUF,
              rules: [{required: true, message: locales.mandatory}],
            })(
              <Select style={{width: 64}} disabled={disabled}>
                {getCurrencyOptions().map(opt =>
                  <Select.Option key={opt.key} value={opt.value}>{opt.title}</Select.Option>
                )}
              </Select>
            )}
          </span>
        </NumberFormItem>

        {isEur &&
          <>
            <NumberFormItem
              form={form}
              fieldId={FIELD_ACQUISITION_EXCHANGE_RATE}
              label={locales.exchange_rate}
              initialValue={item?.acquisitionExchangeRate}
              required={acquisitionValue != null}
              disabled={disabled}
              min={0.01}
              precision={2}
              parser={numberGroupParser}
              formatter={numberGroupFormatter}
            >
              <ExchangeRateRefreshButton
                tooltip={locales.item_exchange_rate_refresh_tooltip}
                disabled={disabled}
                onRefresh={this.refreshExchangeRate}
              />
            </NumberFormItem>
            <StaticFormItem
              label={locales.item_acquisition_value_huf}
              initialValue={hufValue != null ? <>{groupNumber(hufValue)} {locales.app_currency}</> : '-'}
            />
          </>
        }

        <NumberFormItem
          form={form}
          fieldId={FIELD_ITEM_VALUE}
          label={locales.item_value}
          initialValue={item ? item.value : null}
          required={false}
          disabled={disabled}
          min={0}
          max={1_000_000_000}
          precision={0}
          formatter={numberGroupFormatter}
        >
          <> {locales.app_currency}</>
        </NumberFormItem>


        <CheckBoxFormItem
          style={infiniteStockFieldStyle}
          form={form}
          fieldId={FIELD_EXTERNAL}
          label={locales.item_external}
          initialValue={item ? item.external : false}
          required={false}
          disabled={disabled}
        />

        {external &&
            <ClientNameAutoCompleteFormItem
                style={infiniteStockFieldStyle}
                form={form}
                fieldId={FIELD_EXTERNAL_OWNER}
                label={locales.item_external_owner}
                initialValue={item ? item.externalOwner : null}
                required={true}
                disabled={disabled}
                validateOnDataSource={true}
            />
        }


        <SectionHeaderFormItem title={locales.invoicing}/>

        <InputFormItem
          form={form}
          fieldId={FIELD_INVOICING_SUFFIX}
          label={locales.invoicing_suffix}
          initialValue={item ? item.invoicingSuffix : locales.invoicing_suffix_default}
          required={false}
          disabled={disabled}
        />

        <SectionHeaderFormItem title={locales.fees}/>

        {this.isDeviceItemType() && (
          <>
            <NumberFormItem
              form={form}
              fieldId={FIELD_PRICE_DAILY}
              label={locales.price_daily}
              initialValue={item ? item.priceDaily : null}
              required={false}
              disabled={disabled}
              min={0}
              max={100_000_000}
              precision={0}
              formatter={numberGroupFormatter}
            >
              <> {locales.app_currency}</>
            </NumberFormItem>

            <NumberFormItem
              form={form}
              fieldId={FIELD_PRICE_MONTHLY}
              label={locales.price_monthly}
              initialValue={item ? item.priceMonthly : null}
              required={false}
              disabled={disabled}
              min={0}
              max={100_000_000}
              precision={0}
              formatter={numberGroupFormatter}
            >
              <> {locales.app_currency}</>
            </NumberFormItem>

            <NumberFormItem
              form={form}
              fieldId={FIELD_PRICE_MM}
              label={locales.price_mm}
              initialValue={item ? item.priceMm : null}
              required={false}
              disabled={disabled}
              min={0}
              max={100_000_000}
              precision={0}
              formatter={numberGroupFormatter}
            >
              <> {locales.app_currency}</>
              <div style={{display: 'block'}}><Tag color="blue">{locales.where_applicable}</Tag></div>
            </NumberFormItem>
          </>
        )}

        <NumberFormItem
          form={form}
          fieldId={FIELD_PRICE_PCS}
          label={locales.price_pcs}
          initialValue={item ? item.pricePcs : null}
          required={false}
          disabled={disabled}
          min={0}
          max={100_000_000}
          precision={0}
          formatter={numberGroupFormatter}
        >
          <> {locales.app_currency}</>
        </NumberFormItem>

        <NumberFormItem
          form={form}
          fieldId={FIELD_PRICE_HOURLY}
          label={locales.price_hourly}
          initialValue={item ? item.priceHourly : null}
          required={false}
          disabled={disabled}
          min={0}
          max={100_000_000}
          precision={0}
          formatter={numberGroupFormatter}
        >
          <> {locales.app_currency}</>
        </NumberFormItem>

        <SubmitCancelFormItem disabled={disabled} onSubmit={this.onFormSubmit} onCancel={this.onFormCancel}/>
      </Form>
    );
  }

  private refreshExchangeRate = () => {
    return this.props.onRefreshExchangeRate()
      .then(rate => {
        if (rate != null && this.props.editable && this.props.form.getFieldValue(FIELD_ACQUISITION_CURRENCY) === CURRENCY_EUR) {
          this.props.form.setFieldsValue({[FIELD_ACQUISITION_EXCHANGE_RATE]: rate});
        }
      });
  };

  private isDeviceItemType = () => {
    return this.props.form.getFieldValue(FIELD_ITEM_TYPE) === ItemType.DEVICE;
  };

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {item, form, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onFormSubmit({
          ...item,
          itemType: values[FIELD_ITEM_TYPE],
          inventoryNumber: values[FIELD_INVENTORY_NUMBER],
          name: values[FIELD_NAME],
          category: values[FIELD_CATEGORY],
          resource: values[FIELD_RESOURCE],
          details: values[FIELD_DETAILS],
          comment: values[FIELD_COMMENT],
          invoicingSuffix: values[FIELD_INVOICING_SUFFIX],
          serial: values[FIELD_SERIAL],
          priceHourly: values[FIELD_PRICE_HOURLY],
          priceDaily: values[FIELD_PRICE_DAILY],
          priceMonthly: values[FIELD_PRICE_MONTHLY],
          priceMm: values[FIELD_PRICE_MM],
          pricePcs: values[FIELD_PRICE_PCS],
          value: values[FIELD_ITEM_VALUE],
          acquisitionValue: values[FIELD_ACQUISITION_VALUE],
          acquisitionCurrency: values[FIELD_ACQUISITION_CURRENCY] || CURRENCY_HUF,
          acquisitionExchangeRate: values[FIELD_ACQUISITION_CURRENCY] === CURRENCY_EUR
            ? values[FIELD_ACQUISITION_EXCHANGE_RATE] : undefined,
          infiniteStock: values[FIELD_INFINITE_STOCK],
          checklistRequired: values[FIELD_CHECKLIST_REQUIRED],
          external: values[FIELD_EXTERNAL],
          externalOwner: values[FIELD_EXTERNAL_OWNER],
        });
      }
    });
  };

}

export default Form.create<Props>()(ItemForm);



// WEBPACK FOOTER //
// ./src/item-details/ItemForm.tsx